/**
 * The receipt this machine prints, held where a receipt can still be printed.
 *
 * **The cache is the point, not an optimisation.** A slip is printed from local
 * data on purpose — that is what lets a card sale hand a customer paper while
 * the uplink is down — and a layout fetched over the network at print time
 * would put the arrangement on the wrong side of the one failure the whole
 * design is built around. So the document lives in IndexedDB beside the outbox
 * and the menu, and the network only ever replaces it.
 *
 * Three answers in order, and none of them is an error: **the cached document**,
 * **the one the server just sent**, and **`DEFAULT_TEMPLATE`** — which is byte
 * for byte the receipt this system printed before any of this existed. A kiosk
 * that has never reached the server prints a correct receipt.
 */
import { zReceiptTemplate } from '@mgc/protocol'
import { DEFAULT_TEMPLATE } from '@mgc/receipt'
import type { ReceiptTemplate } from '@mgc/receipt'
import { trpc } from '@/api/client'
import { getMeta, setMeta } from '@/outbox/outbox'
import { record } from './diagnostics'

const KEY = 'receipt-template'

interface Held {
  readonly branchId: string
  readonly revision: string
  readonly template: unknown
}

/**
 * What is in hand right now.
 *
 * Read synchronously by `printReceipt`, because the print path is the one place
 * that must not wait: the customer is standing there and the money is already
 * taken. `load()` fills it during boot, long before a sale.
 */
let current: ReceiptTemplate = DEFAULT_TEMPLATE
let revision: string | null = null

export const templateInHand = (): ReceiptTemplate => current

/** For tests, and for a kiosk that has just been re-enrolled into another shop. */
export function forgetTemplate(): void {
  current = DEFAULT_TEMPLATE
  revision = null
}

/**
 * Read the cache. Called before the network, so a cold offline boot still
 * prints the shop's own receipt rather than the built-in one.
 *
 * A cached document is parsed rather than trusted: it was written by a version
 * of this page that may be older than this one, and a document that no longer
 * validates is dropped. Printing a half-understood layout is worse than
 * printing the default — the default is a receipt somebody has seen.
 */
export async function loadCachedTemplate(branchId: string): Promise<void> {
  try {
    const held = await getMeta<Held>(KEY)
    if (held === undefined) return
    // A re-enrolled kiosk must not print the last shop's receipt. The menu
    // cache drops on the same test and for the same reason.
    if (held.branchId !== branchId) return
    const parsed = zReceiptTemplate.safeParse(held.template)
    if (!parsed.success) {
      record('warn', 'the cached receipt template no longer parses; using the built-in one')
      return
    }
    current = parsed.data as ReceiptTemplate
    revision = held.revision
  } catch {
    // No IndexedDB, a private window, a browser that refused. The default is a
    // working receipt and this is not worth a boot failure.
  }
}

/**
 * Ask the server, passing what we hold.
 *
 * A kiosk that is up to date costs a boolean rather than a document with a logo
 * in it — which matters because every kiosk in the fleet asks on every
 * reconnect. Failure is not fatal and is deliberately **not** reported as
 * "offline": whatever we hold is still what we print.
 */
export async function refreshTemplate(branchId: string): Promise<void> {
  try {
    const answer = await trpc.receiptTemplate.mine.query({ known: revision })
    if (answer.unchanged) return

    const parsed = zReceiptTemplate.safeParse(answer.template)
    if (!parsed.success) {
      record('warn', 'the server sent a receipt template this page cannot read; keeping the one in hand')
      return
    }
    current = parsed.data as ReceiptTemplate
    revision = answer.revision
    await setMeta(KEY, { branchId, revision: answer.revision, template: answer.template } satisfies Held)
  } catch {
    record('info', 'the receipt template could not be re-read; printing the one in hand')
  }
}
